import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { formStyles } from '../../../constants/SiteVariables';
import { getRoute, loading } from '../../../actions/sessions';
import { asyncActions, clearErrors } from '../../../actions/general';

import {
  createPaymentMethod,
  generateBankAgreement
} from '../../../actions/payment_methods';

import Loading from '../../shared/Loading';
import PaymentMethodRecord from '../../../records/payment_method';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity'
import Error from '../../shared/Error';
import ColumnInput from '../../shared/ColumnInput';
import StickyInput from '../../shared/StickyInput';
import Heap from '../../../lib/heap';

const heap = new Heap('BankCredentials');

export default function BankCredentials({location}) {
  const dispatch = useDispatch();

  const merchant = useSessionEntity('merchant');
  const user = useSessionEntity('user');
  const isLoading = useSelector(state => state.sessions.get('loading'));
  const session = useSelector(state => state.sessions.get('session'));
  const asyncErrors = useSelector(state => state.general.get('errors'));
  const [bankAgreement, setBankAgreement] = useState(null);
  const [bankProblem, setBankProblem] = useState(false);
  const [checkSrc, setCheckSrc] = useState('default');
  const form = useRef(null);

  useRouteStatus(location);

  useEffect(() => {
    const invalid = asyncErrors.find(e => e && e.includes('invalid_bank'));
    const excessive = asyncErrors.find(e => e && e.includes('excessive_attempts'));

    if (excessive) {
      heap.track({merchant: merchant, session, user: user, payload: {invalid: true}});

      dispatch(getRoute())
        .then(() => dispatch(loading(false)));
    } else if (invalid) {
      heap.track({merchant: merchant, session, user: user, payload: {failed: true}});

      window.scrollTo(0, 0);

      setBankProblem(true);
      form.current.setErrors({
        bank_account: 'Double check the account number and try again.',
        bank_routing: 'Double check the routing number and try again.'
      });

      dispatch(loading(false));
    }

    dispatch(clearErrors());
  }, [asyncErrors]);

  useEffect(() => {
    dispatch(loading(true));
    dispatch(asyncActions(
      generateBankAgreement(user)
    ))
    .then(res => setBankAgreement({ __html: res.attributes.data }))
    .then(() => dispatch(loading(false)));
  }, []);

  const toggleCheck = input => {
    setCheckSrc(input);
  };

  const save = values => {
    dispatch(loading(true));
    return dispatch(asyncActions(
      createPaymentMethod(
        new PaymentMethodRecord(values),
        session.id
      ),
      getRoute()
    ))
    .then(() => dispatch(loading(false)));
  };

  if (!user) return <Loading active={true} />;

  return (
    <div id="content">
      <div className="has-text-centered mb-5">
        <div className="is-size-4 has-text-weight-semibold">Last Step!</div>
        Please confirm your business bank details:
      </div>

      <Error />

      <div className="columns">
        <div className="column">
          <div role="error"
            className={classNames('notification is-danger', {
              'is-hidden': !bankProblem
            })}>
            There was a problem validating your bank. Please double check the routing and account number and try again.
          </div>

          <Formik
            innerRef={form}
            initialValues={{
              agree: '',
              bank_routing: '',
              bank_account: '',
              account_holder: '',
              user_id: user.id
            }}
            validate={async (values) => {
              const errors = {}

              if (!values.agree) errors.agree = '* Required';

              if (!values.bank_routing) {
                errors.bank_routing = '* Required';
              } else if (values.bank_routing.trim().length < 9) {
                errors.bank_routing = 'Routing No. must be exactly 9 characters long';
              }

              if (!values.account_holder) errors.account_holder = '* Required';
              if (!values.bank_account) errors.bank_account = '* Required';

              return errors;
            }}
            onSubmit={save}
          >
            {({ isSubmitting, errors }) => (
              <Form>
                <div className="mb-3">
                  <img src={'https://creditkey-assets.s3-us-west-2.amazonaws.com/ck-check-assets/check-' + checkSrc + '.svg'} style={{ width: '100%', height: '217px' }} />
                </div>

                <ColumnInput error={errors.bank_routing}>
                  <StickyInput
                    id="bank_routing"
                    inputMode="numeric"
                    mask="999999999"
                    placeholder="Routing Number"
                    onFocus={() => toggleCheck('routing')}
                    styles="is-mobile"
                  />
                </ColumnInput>

                <ColumnInput error={errors.bank_account}>
                  <StickyInput
                    id="bank_account"
                    inputMode="numeric"
                    mask={/^\d*$/}
                    placeholder="Account Number"
                    onFocus={() => toggleCheck('account')}
                    styles="is-mobile"
                  />
                </ColumnInput>

                <ColumnInput error={errors.account_holder}>
                  <StickyInput
                    id="account_holder"
                    placeholder="Name on Account"
                    onFocus={() => toggleCheck('default')}
                    styles="is-mobile"
                  />
                </ColumnInput>

                <div className="columns is-mobile mb-0">
                  <div className="column">
                    <div dangerouslySetInnerHTML={bankAgreement}></div>
                  </div>
                </div>

                <div className="columns is-mobile mb-0">
                  <div className="column">
                    <div className="field">
                      <Field
                        className={classNames('is-checkradio', {
                          'is-danger': errors.agree
                        })}
                        type="checkbox"
                        id="agree"
                        name="agree"
                        value="yes" />
                      <label htmlFor="agree">
                          I authorize Credit Key to charge scheduled charges to my bank account.
                      </label>
                      <ErrorMessage name="agree" component="div" className={formStyles.validation} />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  role="submit"
                  disabled={isLoading}
                  className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
                    'is-loading': isLoading
                  })}>
                  Next
                </button>
              </Form>
            )}
          </Formik>

        </div>
      </div>

    </div>
  )
}